import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    MenuItem,
    Select,
    Stack,
    Tab,
    Typography,
    useTheme,
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { TextReplacementsCard } from '@/pageContents/textReplacements/TextReplacementCard';
import {
    useLazyGetTextReplacementQuery,
    usePostTextReplacementsMutation,
} from '@/api/v2/textReplacement';

import {
    ProductTextReplacement,
    TextReplacementBlock,
    TextReplacementIndex,
    TextReplacementIndexKey,
} from '@/models/textReplacement';
import { cloneDeep, debounce } from 'lodash';
import { useNotification } from '@starjack/features/notifications';
import { useSlice } from '@/hooks/useSlice';
import { pageDataSlice } from '@starjack/features/pageData';
import { useGetLiftOperatorsQuery } from '@/api/v2/liftOperator';
import { LiftOperator } from '@/models/liftoperator';

type TabValue = TextReplacementIndexKey | 'product';

export const MUTATION_CACHE_KEY = 'TEXT_REPLACEMENT';

const filterFn = (text: string, searchText: string): boolean => {
    if (!text || !searchText) return false;

    const normalizedText = text.trim().toLocaleLowerCase();
    const normalizedSearchText = searchText.trim().toLocaleLowerCase();
    const splitted = normalizedText.split(' ');
    const searchSplitted = normalizedSearchText.split(' ');

    return searchSplitted.every((searchSplit) => {
        return splitted.some((split) => split.includes(searchSplit));
    });
};

const searchComparison = (
    textBlocks: TextReplacementBlock[],
    searchTerm = ''
) => {
    return textBlocks.filter(
        (textBlock) =>
            filterFn(textBlock.oldText, searchTerm) ||
            filterFn(textBlock.newText, searchTerm)
    );
};

export const TextReplacementContent: FunctionComponent = () => {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState<TabValue>(null);
    const { data: liftOperators } = useGetLiftOperatorsQuery();

    const [getTextReplacements, { data }] = useLazyGetTextReplacementQuery();
    const [postTextReplacements] = usePostTextReplacementsMutation({
        fixedCacheKey: MUTATION_CACHE_KEY,
    });
    const [textReplacementIndex, setTextReplacementIndex] =
        useState<TextReplacementIndex>(null);
    const [productTextReplacements, setProductTextReplacements] = useState<
        ProductTextReplacement[]
    >(data?.productTextReplacements);
    const { addNotification } = useNotification();
    const {
        actions: { setPageData },
    } = useSlice(pageDataSlice, 'requestSlice');
    const [selectedLiftOperator, setSelectedLiftOperator] =
        useState<LiftOperator>(null);

    const handleTabChange = (_, newValue: TabValue) => {
        // reset search
        setTextReplacementIndex(data?.textReplacementIndex);

        setTabValue(newValue);
    };

    const onSearch = useMemo(
        () => (replacementKeyIndex: TextReplacementIndexKey) =>
            debounce((searchTerm) => {
                if (searchTerm) {
                    setTextReplacementIndex(() => ({
                        [replacementKeyIndex]: searchComparison(
                            data?.textReplacementIndex[replacementKeyIndex] ||
                                [],
                            searchTerm
                        ),
                    }));
                } else {
                    setTextReplacementIndex(data?.textReplacementIndex);
                }
            }, 350),
        [data]
    );

    const handleChange =
        (textReplacementIndexKey: TextReplacementIndexKey) =>
        (newValue: string, index: number) => {
            const cloned = cloneDeep(textReplacementIndex);
            cloned[textReplacementIndexKey][index] = {
                ...cloned[textReplacementIndexKey][index],
                newText: newValue,
            };

            setTextReplacementIndex(cloned);
        };

    const onSave = () => {
        if (!selectedLiftOperator) {
            return;
        }

        const cleanedTextReplacements = Object.entries(
            textReplacementIndex
        ).reduce((textReplacements, [type, textBlocks]) => {
            return [
                ...textReplacements,
                ...textBlocks.map((block) => ({ type, ...block })),
            ];
        }, []);

        postTextReplacements({
            liftOperatorId: selectedLiftOperator.id,
            textReplacements: cleanedTextReplacements,
            productTextReplacements: productTextReplacements,
        })
            .unwrap()
            .then(() => {
                addNotification({
                    type: 'success',
                    message: 'Änderung erfolgreich gespeichert',
                });
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Speichern. Bitte später noch einmal versuchen',
                });
            });
    };

    useEffect(() => {
        setPageData({
            title: 'Textanpassungen Tickets',
            body: (
                <>
                    <Typography gutterBottom>
                        Hier stehen die Personen- und Ticketbezeichnungen, wie
                        sie aktuell über das Zutrittssystem an starjack
                        übermittelt werden. Sie lassen sich hier für den
                        Onlineverkauf überschreiben. Bitte deutsche und
                        englische Sprachversion beachten! Mit der
                        „Produktkombination“ lassen sich einzelne
                        Personen-Ticket-Kombinationen noch detaillierter
                        textlich anpassen und auch die englische Version
                        ergänzen.
                    </Typography>

                    <Typography>
                        <Typography
                            sx={{
                                fontWeight: 500,
                            }}
                            component='span'
                        >
                            Tipp:{' '}
                        </Typography>
                        Für ausführliche Produktbeschreibungen das Feld
                        „Ticketinfo“ im Menü „Produktkonfiguration“ nutzen.
                    </Typography>
                </>
            ),
        });
    }, []);

    useEffect(() => {
        if (data) {
            setTextReplacementIndex(data.textReplacementIndex);
            setProductTextReplacements(data.productTextReplacements);
        }
    }, [data]);

    useEffect(() => {
        if (!selectedLiftOperator) {
            setTabValue(null);
        } else {
            getTextReplacements({ liftOperatorId: selectedLiftOperator.id });
            if (!tabValue) {
                setTabValue(TextReplacementIndexKey.PERSON_NAME);
            }
        }
    }, [selectedLiftOperator]);

    return (
        <TabContext value={tabValue}>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Textanpassungen Tickets
            </Typography>

            <Stack spacing={theme.page.verticalSpacing}>
                <Card>
                    <CardContent>
                        <Select
                            onChange={(e) => {
                                const { value } = e.target;

                                if (value !== '--') {
                                    const lO = liftOperators.find(
                                        (lO) => lO.id === value
                                    );

                                    if (lO) {
                                        setSelectedLiftOperator(lO);

                                        return;
                                    }
                                }

                                setSelectedLiftOperator(null);
                            }}
                            value={selectedLiftOperator?.id || '--'}
                            fullWidth
                        >
                            <MenuItem value='--'>Bergbahn auswählen</MenuItem>
                            {liftOperators?.map((lO) => (
                                <MenuItem key={lO.id} value={lO.id}>
                                    {lO.name.de}
                                </MenuItem>
                            ))}
                        </Select>
                    </CardContent>
                </Card>

                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabList onChange={handleTabChange}>
                        <Tab
                            label='Personenbezeichnung'
                            value={TextReplacementIndexKey.PERSON_NAME}
                            disabled={!selectedLiftOperator}
                        />
                        <Tab
                            label='Ticketbezeichnung'
                            value={TextReplacementIndexKey.TICKET_NAME}
                            disabled={!selectedLiftOperator}
                        />
                    </TabList>
                </Box>

                {!!textReplacementIndex && (
                    <>
                        <TabPanel value={TextReplacementIndexKey.PERSON_NAME}>
                            <TextReplacementsCard
                                onInputBlur={handleChange(
                                    TextReplacementIndexKey.PERSON_NAME
                                )}
                                onSearch={onSearch(
                                    TextReplacementIndexKey.PERSON_NAME
                                )}
                                textReplacementBlocks={
                                    textReplacementIndex[
                                        TextReplacementIndexKey.PERSON_NAME
                                    ]
                                }
                                onSave={onSave}
                            />
                        </TabPanel>

                        <TabPanel value={TextReplacementIndexKey.TICKET_NAME}>
                            <TextReplacementsCard
                                onInputBlur={handleChange(
                                    TextReplacementIndexKey.TICKET_NAME
                                )}
                                onSearch={onSearch(
                                    TextReplacementIndexKey.TICKET_NAME
                                )}
                                textReplacementBlocks={
                                    textReplacementIndex[
                                        TextReplacementIndexKey.TICKET_NAME
                                    ]
                                }
                                onSave={onSave}
                            />
                        </TabPanel>
                    </>
                )}
            </Stack>
        </TabContext>
    );
};
